import { useMemo } from 'react';
import { useFolderTree } from '@/hooks/useFolderTree';
import { partieAdverseTargetKind, type DestinationValue } from './destinationModel';

/**
 * Le `document_subcategory` à écrire avec la pièce, dérivé de la destination.
 *
 * WHY un hook et pas un calcul chez l'appelant : le tag ne se lit ni dans le
 * chemin ni dans le nom du dossier — il vit sur la LIGNE du dossier racine, donc
 * dans l'arborescence. Le placer ici le donne aux quatre écrans d'un coup ; sans
 * lui, chacun devait rouvrir l'arbre pour la même question, et trois sur quatre
 * ne le faisaient pas : ils écrivaient `null` sous « Partie adverse », c'est-à-dire
 * une pièce que l'onglet ne liste dans aucun de ses deux dossiers.
 *
 * WHY aucune requête ajoutée : `useFolderTree(caseId, category)` est la MÊME clé
 * que celle du sélecteur monté juste en dessous — même entrée de cache.
 *
 * Rend `null` hors « Partie adverse » : ailleurs la sous-catégorie est un attribut
 * libre que rien ne lit, et l'inventer brouillerait les lecteurs backend.
 */
export function useDestinationSubcategory(value: DestinationValue): string | null {
  const { tree } = useFolderTree(value.caseId, value.category);
  return useMemo(() => partieAdverseTargetKind(value, tree) ?? null, [value, tree]);
}
